import React, { memo } from 'react';
import {
  LegalContainer,
  LegalText,
  ButtonGroup,
  SectionWrapper,
  MainHeader,
} from './components';
import {
  cssSpacing,
  screenMdMin,
  textClass,
} from '@spotify-internal/encore-web/core';
import Button from './Button';
import {
  eventParamAttributes,
  EventParamTypes,
  inViewEventAttributes,
} from '@spotify-internal/touchdown-analytics';
import { purify } from '../../lib/purify';
import styled from 'styled-components';
import Container from './Container';

type ErrorHeroType = {
  container?: React.ComponentProps<typeof Container>;
  isFullHeight?: boolean;
  header?: { text: string };
  subheaders?: { text: string }[];
  buttons?: React.ComponentProps<typeof Button>[];
  legalDisclaimer?: string;
  textColor?: string;
  eventParams?: EventParamTypes;
  label?: { text: string };
};

const HeaderText = styled.h1`
  text-wrap: wrap;
  margin: 0 0 ${cssSpacing('tighter-2')};

  @media (min-width: ${screenMdMin}) {
    font-size: 64px;
  }
`;

const SubheaderText = styled.h2`
  @media (min-width: ${screenMdMin}) {
    font-size: 24px;
  }
`;

export function ErrorHero(props: ErrorHeroType) {
  const {
    container,
    header,
    subheaders,
    buttons,
    legalDisclaimer,
    eventParams,
    isFullHeight,
  } = props;

  const renderButton = (
    button: React.ComponentProps<typeof Button>,
    index: number,
  ) => {
    const sharedProps = {
      key: `button-${index}`,
      ...button,
    };
    return <Button {...sharedProps} />;
  };

  return (
    <SectionWrapper
      {...{ ...container, isFullHeight }}
      {...eventParamAttributes({ ...eventParams, componentType: 'promo' })}
      {...inViewEventAttributes()}
    >
      <MainHeader>
        {header && (
          <div>
            <HeaderText
              className={textClass('titleLarge')}
              dangerouslySetInnerHTML={{
                __html: purify(header.text),
              }}
            />
          </div>
        )}
        {subheaders &&
          subheaders.map((subheader, index) => (
            <div key={`subheader-${index}`}>
              <SubheaderText
                className={textClass('bodyMedium')}
                dangerouslySetInnerHTML={{
                  __html: purify(subheader.text),
                }}
              />
            </div>
          ))}
      </MainHeader>
      {legalDisclaimer && (
        <LegalContainer>
          <LegalText text={legalDisclaimer} />
        </LegalContainer>
      )}
      {buttons && (
        <ButtonGroup>
          {buttons.map((button, index) => renderButton(button, index))}
        </ButtonGroup>
      )}
    </SectionWrapper>
  );
}

export default memo(ErrorHero);
